import { Button } from '@ibsys/kit';
import { ComponentProps, ComponentType, FC, ReactElement } from 'react';
import { cn, destructiveButtonClass } from '@/shared/lib';
import { Tooltip } from '../Tooltip';

/**
 * Триггер окна (`DialogTrigger`, `PopoverTrigger`), которому кнопка подчиняется: он передаёт ей
 * свои свойства через `asChild`, поэтому встаёт вплотную к кнопке, а подсказка остаётся снаружи.
 */
type TriggerComponent = ComponentType<{ asChild?: boolean; children: ReactElement }>;

type Props = Omit<ComponentProps<typeof Button>, 'aria-label' | 'tooltip' | 'children'> & {
  /** Название действия: и доступное имя кнопки, и её подсказка при наведении */
  label: string;
  /** Триггер окна, которое открывает кнопка; без него кнопка сама по себе */
  trigger?: TriggerComponent;
};

/**
 * Кнопка, обозначенная только значком.
 *
 * Название действия задаётся один раз и служит сразу и доступным именем, и подсказкой: без
 * подписи и без подсказки такая кнопка опознаётся только чтением с экрана, а глазами — никак.
 * Поэтому кнопку-значок собирают этим компонентом, а не парой «`aria-label` + подсказка вручную»:
 * иначе каждое новое место решает вопрос заново и половина кнопок остаётся неподписанной.
 *
 * Кнопка, открывающая окно, собирается тем же компонентом — триггер окна передаётся пропом
 * `trigger`: вставить его между подсказкой и кнопкой у места применения нельзя, не собрав ту же
 * пару вручную, а собранная вручную пара и есть то, от чего компонент избавляет.
 */
export const IconButton: FC<Props> = ({ label, variant = 'ghost', trigger: Trigger, className, ...buttonProps }) => {
  const button = (
    <Button
      aria-label={label}
      variant={variant}
      {...buttonProps}
      className={cn(destructiveButtonClass({ color: buttonProps.color, variant }), className)}
    />
  );

  return <Tooltip tooltip={label}>{Trigger ? <Trigger asChild>{button}</Trigger> : button}</Tooltip>;
};
